import { ArrowLeft, Home, CheckCircle, Shield, FileSearch, Users } from "lucide-react";
import { useNavigate } from "react-router-dom";

const HomeBuyerAdvisoryPage = () => {
  const navigate = useNavigate();

  const features = [
    {
      icon: FileSearch,
      title: "Project Verification",
      description: "Comprehensive verification of RERA registration, approvals, and developer credentials."
    },
    {
      icon: Shield,
      title: "Legal Due Diligence",
      description: "Thorough review of land titles, encumbrances, and legal compliance of the property."
    },
    {
      icon: CheckCircle,
      title: "Documentation Guidance",
      description: "Expert assistance with all documentation required for property purchase."
    },
    {
      icon: Users,
      title: "Personalized Consultation",
      description: "One-on-one sessions with our RERA experts to address your specific concerns."
    }
  ];

  return (
    <div className="min-h-screen fade-in" data-testid="advisory-services-page">
      {/* Hero Section */}
      <section className="pt-32 pb-16 md:pb-20 section-light" data-testid="advisory-hero">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-20">
          {/* Back Button */}
          <button
            onClick={() => navigate("/home-buyer")}
            className="inline-flex items-center gap-2 text-[#555555] hover:text-[#C9A14A] transition-colors duration-300 mb-8"
          >
            <ArrowLeft className="w-4 h-4" />
            <span className="text-sm font-medium">Back to Home Buyer</span>
          </button>

          <div className="max-w-3xl">
            <div className="w-16 h-16 rounded-full bg-[#C9A14A]/10 flex items-center justify-center mb-6">
              <Home className="w-8 h-8 text-[#C9A14A]" />
            </div>
            <h1
              className="text-3xl sm:text-4xl md:text-5xl tracking-tight leading-tight text-[#1A1A1A] mb-6"
              style={{ fontFamily: "'Playfair Display', serif" }}
            >
              Home Buyer RERA Advisory Services
            </h1>
            <p className="text-base sm:text-lg md:text-xl leading-relaxed text-[#555555]">
              Get the right guidance and essential information to make informed property-buying decisions. Our expert team helps you navigate every step of the home buying process.
            </p>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-16 md:py-24 section-grey" data-testid="advisory-features">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-20">
          <div className="text-center mb-12 md:mb-16">
            <h2
              className="text-2xl sm:text-3xl md:text-4xl tracking-tight leading-snug text-[#1A1A1A] mb-4"
              style={{ fontFamily: "'Playfair Display', serif" }}
            >
              What We Offer
            </h2>
            <p className="text-sm sm:text-base md:text-lg text-[#555555] max-w-2xl mx-auto">
              Comprehensive advisory services to protect your interests and ensure a smooth property purchase.
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 md:gap-8">
            {features.map((feature, index) => {
              const Icon = feature.icon;
              return (
                <div
                  key={index}
                  className="bg-white p-6 sm:p-8 rounded-xl shadow-sm border border-slate-100 hover:shadow-md hover:border-[#C9A14A]/30 transition-all duration-300"
                >
                  <Icon className="w-10 h-10 md:w-12 md:h-12 text-[#C9A14A] mb-4" />
                  <h3
                    className="text-lg sm:text-xl font-semibold text-[#1A1A1A] mb-2"
                    style={{ fontFamily: "'Playfair Display', serif" }}
                  >
                    {feature.title}
                  </h3>
                  <p className="text-sm sm:text-base text-[#555555] leading-relaxed">
                    {feature.description}
                  </p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Content Placeholder Section */}
      <section className="py-16 md:py-24 section-light" data-testid="advisory-content">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-20">
          <div className="bg-white p-8 sm:p-10 md:p-14 rounded-xl shadow-sm border border-slate-100">
            <div className="max-w-3xl mx-auto text-center">
              <h2
                className="text-2xl sm:text-3xl md:text-4xl tracking-tight leading-snug text-[#1A1A1A] mb-4"
                style={{ fontFamily: "'Playfair Display', serif" }}
              >
                Why Choose Our Advisory Services?
              </h2>
              <p className="text-sm sm:text-base md:text-lg text-[#555555] leading-relaxed mb-8">
                With years of experience in RERA compliance and real estate advisory, we provide unbiased, expert guidance to help you make the right property decisions. Our team understands the complexities of property transactions and ensures you have all the information needed to proceed confidently.
              </p>
              <button
                onClick={() => navigate("/contact")}
                className="btn-accent px-6 sm:px-8 py-3 sm:py-4 rounded-md font-medium text-sm sm:text-base"
              >
                Get Started
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 md:py-20 section-grey" data-testid="advisory-cta">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-20 text-center">
          <h2
            className="text-2xl sm:text-3xl md:text-4xl tracking-tight leading-snug text-[#1A1A1A] mb-4"
            style={{ fontFamily: "'Playfair Display', serif" }}
          >
            Ready to Get Expert Guidance?
          </h2>
          <p className="text-sm sm:text-base md:text-lg text-[#555555] mb-8 max-w-2xl mx-auto">
            Contact us today to schedule a consultation with our RERA experts.
          </p>
          <a
            href="https://wa.me/919404515431?text=Hello%20ReraHub%2C%20I%20would%20like%20to%20get%20expert%20guidance%20regarding%20RERA%20services."
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center justify-center gap-2 bg-[#25D366] hover:bg-[#1ebe5d] text-white px-8 py-4 rounded-full font-medium text-base shadow-md hover:shadow-lg transition-all duration-300"
            data-testid="whatsapp-cta-btn"
          >
            <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
              <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
            </svg>
            Chat on WhatsApp
          </a>
        </div>
      </section>
    </div>
  );
};

export default HomeBuyerAdvisoryPage;
